import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Loader2, FileStack, Ticket, AlertTriangle, Image as ImageIcon, FileText, FolderArchive, Download, MapPin, Calendar, Ruler, Printer, Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import PageHelp from "@/components/PageHelp";
import AuthImage from "@/components/AuthImage";
import Lightbox from "@/components/Lightbox";
import PendingPhotoBadge from "@/components/PendingPhotoBadge";
import PendingItemBadge from "@/components/PendingItemBadge";
import UploadStateBadge from "@/components/UploadStateBadge";
import PhotoReportBar from "@/components/PhotoReportBar";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import NewTicketDialog from "@/components/NewTicketDialog";
import ProjectTicketDialog from "@/components/ProjectTicketDialog";
import { Plus } from "lucide-react";

const ST = {
  offen: "bg-slate-100 text-slate-700 border-slate-200",
  in_bearbeitung: "bg-sky-50 text-sky-700 border-sky-200",
  behoben: "bg-emerald-50 text-emerald-700 border-emerald-200",
  erledigt: "bg-emerald-50 text-emerald-700 border-emerald-200",
  abgenommen: "bg-emerald-50 text-emerald-700 border-emerald-200",
  geschlossen: "bg-slate-100 text-slate-700 border-slate-200",
};
const stClass = (s) => ST[s] || "bg-slate-100 text-slate-700 border-slate-200";

const CONFIG = {
  plans: { title: "Baupläne", icon: FileStack, help: "Alle Baupläne projektübergreifend. Öffnen Sie einen Plan, um Marker bzw. Positionen zu setzen und Details zu erfassen.", load: () => api.get("/overview/plans").then((r) => r.data) },
  tickets: { title: "Tickets", icon: Ticket, help: "Alle Tickets und Aufgaben über sämtliche Projekte hinweg – nach Status, Frist und Projekt sortiert.", load: () => api.get("/search?kind=tickets").then((r) => r.data.tickets || []) },
  defects: { title: "Mängel", icon: AlertTriangle, help: "Alle erfassten Mängel projektübergreifend. Öffnen Sie einen Mangel, um Fotos, Maßnahmen und Status zu pflegen.", load: () => api.get("/search?kind=defects").then((r) => r.data.defects || []) },
  photos: { title: "Fotos", icon: ImageIcon, help: "Alle Fotos und Medien an einem Ort – inklusive Mangelfotos und Vorher/Nachher-Aufnahmen.", load: () => api.get("/media?category=photo,defect_photo").then((r) => r.data) },
  reports: { title: "Berichte", icon: FileText, help: "Prüftagesberichte und der PDF-Export je Projekt. Erzeugen Sie hier die fertige Dokumentation zum Teilen.", load: () => api.get("/overview/daily-reports").then((r) => r.data) },
  documents: { title: "Dokumente", icon: FolderArchive, help: "Zusätzliche Projektdateien zum Ablegen, Öffnen und Herunterladen.", load: () => api.get("/overview/documents").then((r) => r.data) },
};

export default function Overview({ view }) {
  const cfg = CONFIG[view];
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [quantities, setQuantities] = useState([]);
  const [exporting, setExporting] = useState("");
  const [exportingAll, setExportingAll] = useState("");
  const [lightbox, setLightbox] = useState(null); // index into items (photos view)
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const photoIds = view === "photos" && items ? items.filter((f) => !f.__pending).map((f) => f.id) : [];
  const toggleSel = (f) => { if (f.__pending) return; setSelected((s) => (s.includes(f.id) ? s.filter((x) => x !== f.id) : [...s, f.id])); };
  const [newTicket, setNewTicket] = useState(false);
  const [openTicket, setOpenTicket] = useState(null);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (view !== "reports") return;
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/overview/quantities").then((r) => setQuantities(r.data)).catch(() => {});
  }, [view]);
  const downloadAllDefects = async (onlyOpen = false) => {
    setExportingAll(onlyOpen ? "open" : "all");
    try {
      const res = await api.get(`/overview/defects/report${onlyOpen ? "?status=open" : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = onlyOpen ? "Offene_Maengel.pdf" : "Kompletter_Maengelbericht.pdf"; a.click();
      URL.revokeObjectURL(url);
      toast.success(onlyOpen ? "Bericht (nur offene Mängel) erstellt" : "Mängelbericht erstellt");
    } catch (err) {
      console.warn("Mängelbericht-Export fehlgeschlagen", err?.response?.status || err?.message);
      toast.error("Mängelbericht-Export fehlgeschlagen");
    } finally { setExportingAll(""); }
  };
  const downloadTextReport = async () => {
    setExportingAll("text");
    try {
      const res = await api.get("/overview/defects/report?text_only=true", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Maengelbericht_Text_${new Date().toISOString().slice(0, 10)}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Mängelbericht (Text) erstellt");
    } catch (err) {
      console.warn("Text-Mängelbericht fehlgeschlagen", err?.response?.status || err?.message);
      toast.error("Mängelbericht (Text) fehlgeschlagen");
    } finally { setExportingAll(""); }
  };
  const exportProject = async (p) => {
    setExporting(p.id);
    try {
      const res = await api.post(`/projects/${p.id}/report`, { template: "vollstaendig" }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Baudokumentation_${p.name}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { console.warn("Projekt-PDF Export fehlgeschlagen", err?.response?.status || err?.message); toast.error("PDF-Export fehlgeschlagen"); } finally { setExporting(""); }
  };

  useEffect(() => {
    let alive = true;
    setItems(null);
    cfg.load().then((d) => alive && setItems(d || [])).catch(() => alive && setItems([]));
    return () => { alive = false; };
  }, [view, reload]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!["photos", "defects", "tickets"].includes(view)) return undefined;
    const refresh = () => cfg.load().then((d) => setItems(d || [])).catch(() => {});
    window.addEventListener("offline-queued", refresh);
    return () => window.removeEventListener("offline-queued", refresh);
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  const Icon = cfg.icon;

  return (
    <div className="fade-up" data-testid="overview-page">
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Icon className="h-5 w-5" /></div>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-head font-extrabold text-slate-900">{cfg.title}</h1>
            <PageHelp id={view} title={cfg.title} text={cfg.help} />
          </div>
          <p className="text-slate-500 text-sm">Projektübergreifende Übersicht{items && view !== "reports" ? ` · ${items.length}` : ""}</p>
        </div>
        {view === "tickets" && can(user, "ticket:write") && (
          <Button className="w-full sm:w-auto sm:ml-auto" onClick={() => setNewTicket(true)} data-testid="new-ticket-btn"><Plus className="h-4 w-4 mr-2" />Neues Ticket erstellen</Button>
        )}
        {view === "photos" && !selectMode && (
          <Button variant="outline" className="w-full sm:w-auto sm:ml-auto" onClick={() => { setSelectMode(true); setSelected([]); }} data-testid="photo-report-start">
            <Printer className="h-4 w-4 mr-2" />Fotobericht
          </Button>
        )}
        {view === "defects" && can(user, "reports:create") && (
          <div className="w-full sm:w-auto sm:ml-auto flex items-center gap-2 flex-wrap sm:justify-end">
            <Button variant="outline" onClick={downloadTextReport} disabled={!!exportingAll} data-testid="text-defects-report-btn">
              {exportingAll === "text" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
              Mängelbericht TEXT (PDF)
            </Button>
            <Button variant="outline" onClick={() => downloadAllDefects(true)} disabled={!!exportingAll} data-testid="open-defects-report-btn">
              {exportingAll === "open" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
              Nur offene (PDF)
            </Button>
            <Button onClick={() => downloadAllDefects(false)} disabled={!!exportingAll} data-testid="all-defects-report-btn">
              {exportingAll === "all" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
              Kompletter Mängelbericht (PDF)
            </Button>
          </div>
        )}
      </div>

      {items === null ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : items.length === 0 && view !== "reports" ? (
        <div className="text-center py-20 text-slate-400" data-testid="overview-empty">Keine Einträge vorhanden.</div>
      ) : view === "plans" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <button key={p.id} data-testid={`overview-item-${p.id}`} onClick={() => navigate(`/plans/${p.id}`)}
              className="text-left bg-white rounded-2xl border border-slate-200 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2 mb-2"><FileStack className="h-4 w-4 text-primary" /><span className="font-semibold text-slate-900 truncate">{p.name}</span></div>
              <div className="text-xs text-slate-500">{p.project_name}{p.floor_name ? ` · ${p.floor_name}` : ""}</div>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200"><MapPin className="h-3 w-3 mr-1" />{p.position_count} Positionen</Badge>
                {p.defect_count > 0 && (
                  <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200" data-testid={`overview-plan-defects-${p.id}`}>
                    <AlertTriangle className="h-3 w-3 mr-1" />{p.defect_count} {p.defect_count === 1 ? "Mangel" : "Mängel"}{p.open_defect_count > 0 ? ` · ${p.open_defect_count} offen` : ""}
                  </Badge>
                )}
              </div>
            </button>
          ))}
        </div>
      ) : view === "photos" ? (
        <>
        {selectMode && (
          <PhotoReportBar selected={selected} total={photoIds.length}
            onSelectAll={() => setSelected(selected.length === photoIds.length ? [] : photoIds)}
            onCancel={() => { setSelectMode(false); setSelected([]); }} />
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {items.map((f, idx) => (
            <div key={f.id} data-testid={`overview-item-${f.id}`} className={`group relative rounded-xl overflow-hidden border bg-white transition-shadow hover:shadow-md ${selectMode && selected.includes(f.id) ? "border-primary ring-2 ring-primary" : "border-slate-200"}`}>
              <div onClick={() => (selectMode ? toggleSel(f) : setLightbox(idx))} className="cursor-pointer" title={selectMode ? "Auswählen" : "Groß anzeigen"}>
                <AuthImage fileId={f.id} localUrl={f.__localUrl} thumb alt={f.description || "Foto"} className="w-full h-32 object-cover bg-slate-100" />
              </div>
              {selectMode && !f.__pending && (
                <span data-testid={`photo-select-${f.id}`} onClick={() => toggleSel(f)}
                  className={`absolute top-1.5 left-1.5 h-6 w-6 rounded-md border-2 flex items-center justify-center cursor-pointer shadow ${selected.includes(f.id) ? "bg-primary border-primary text-white" : "bg-white/90 border-slate-300"}`}>
                  {selected.includes(f.id) && <Check className="h-4 w-4" />}
                </span>
              )}
              <UploadStateBadge f={f} />
              {f.defect_id && f.plan_id && (
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(`/plans/${f.plan_id}?position=${f.position_id}&tab=maengel`); }}
                  data-testid={`open-defect-${f.id}`}
                  title="Zugehörigen Mangel öffnen"
                  className="absolute top-1.5 right-1.5 inline-flex items-center gap-1 rounded-full bg-red-600 text-white text-[10px] font-medium px-2 py-1 shadow hover:bg-red-700 transition-colors">
                  <AlertTriangle className="h-3 w-3" />Mangel
                </button>
              )}
              <div onClick={() => (selectMode ? toggleSel(f) : setLightbox(idx))} className="p-2 text-[11px] text-slate-500 truncate cursor-pointer">{(f.taken_at || f.created_at || "").slice(0, 10)}{f.description ? ` · ${f.description}` : ""}</div>
            </div>
          ))}
        </div>
        </>
      ) : view === "documents" ? (
        <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
          {items.map((d) => (
            <div key={d.id} data-testid={`overview-item-${d.id}`} className="flex items-center gap-3 p-3">
              <FileText className="h-5 w-5 text-slate-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-900 truncate flex items-center gap-2">
                  {d.name || d.original_filename || d.filename || d.original_name || d.description || "Dokument"}
                  {d.source === "inspection" && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Prüfung</Badge>}
                </div>
                <div className="text-xs text-slate-500 truncate">{d.project_name || (d.source === "inspection" ? "Ohne Projekt" : "")}{d.inspection_title ? ` · ${d.inspection_title}` : ""} · {(d.created_at || "").slice(0, 10)}</div>
              </div>
              <a href={`${API}/${d.source === "inspection" ? `inspections/documents/${d.id}/download` : `files/${d.id}/download`}`} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600" data-testid={`overview-download-${d.id}`}><Download className="h-4 w-4" /></a>
            </div>
          ))}
        </div>
      ) : view === "reports" ? (
        <div className="space-y-6">
          <div>
            <h2 className="text-sm font-semibold text-slate-700 mb-2">PDF-Export je Projekt</h2>
            <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
              {projects.length === 0 && <div className="p-4 text-sm text-slate-400">Keine Projekte.</div>}
              {projects.map((p) => (
                <div key={p.id} className="flex items-center gap-3 p-3" data-testid={`report-export-${p.id}`}>
                  <FileText className="h-5 w-5 text-primary shrink-0" />
                  <div className="flex-1 min-w-0"><div className="font-medium text-slate-900 truncate">{p.name}</div><div className="text-xs text-slate-500">{p.progress}% · {p.open_tickets} offene Tickets</div></div>
                  <Button size="sm" variant="outline" disabled={exporting === p.id} onClick={() => exportProject(p)} data-testid={`report-export-btn-${p.id}`}>
                    {exporting === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}PDF
                  </Button>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h2 className="text-sm font-semibold text-slate-700 mb-2">Prüftagesberichte ({items.length})</h2>
            <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
              {items.length === 0 && <div className="p-4 text-sm text-slate-400">Keine Tagesberichte.</div>}
              {items.map((r) => (
                <button key={r.id} data-testid={`overview-item-${r.id}`} onClick={() => navigate(`/projects/${r.project_id}`)} className="w-full text-left flex items-center gap-3 p-3 hover:bg-slate-50">
                  <Calendar className="h-5 w-5 text-primary shrink-0" />
                  <div className="min-w-0 flex-1"><div className="font-medium text-slate-900">{(r.date || "").slice(0, 10)}</div><div className="text-xs text-slate-500 truncate">{r.project_name}</div></div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <h2 className="text-sm font-semibold text-slate-700 mb-2">Mengenberichte ({quantities.length})</h2>
            <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
              {quantities.length === 0 && <div className="p-4 text-sm text-slate-400">Keine Mengen erfasst.</div>}
              {quantities.map((q) => (
                <div key={q.id} className="flex items-center gap-3 p-3" data-testid={`overview-qty-${q.id}`}>
                  <Ruler className="h-5 w-5 text-slate-400 shrink-0" />
                  <div className="flex-1 min-w-0"><div className="font-medium text-slate-900 truncate">{q.description || q.service || "Menge"}</div><div className="text-xs text-slate-500 truncate">{q.project_name}{q.company ? ` · ${q.company}` : ""}</div></div>
                  <span className="text-sm font-semibold text-slate-700 shrink-0">{q.amount} {q.unit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : view === "defects" ? (
        <div className="space-y-5" data-testid="defects-by-project">
          {groupByProject(items).map(([name, list], gi) => (
            <section key={name} className={gi ? "pt-5 border-t-2 border-slate-300" : ""} data-testid={`defects-project-group-${gi}`}>
              <h2 className="text-base md:text-lg font-head font-bold text-slate-900 mb-2 flex items-center justify-between gap-2" data-testid={`defects-project-heading-${gi}`}>
                <span className="truncate">{name}</span><span className="text-xs font-normal text-slate-500 shrink-0">{list.length} Mängel</span>
              </h2>
              <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
                {list.map((it) => <OverviewRow key={it.id} it={it} navigate={navigate} view={view} />)}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
          {items.map((it) => <OverviewRow key={it.id} it={it} navigate={navigate} view={view} onOpen={setOpenTicket} />)}
        </div>
      )}
      {view === "tickets" && <NewTicketDialog open={newTicket} onClose={() => setNewTicket(false)} onCreated={() => setReload((n) => n + 1)} />}
      {view === "tickets" && <ProjectTicketDialog ticket={openTicket} onClose={() => setOpenTicket(null)} onChanged={() => setReload((n) => n + 1)} onOpenPlan={(pos) => navigate(`/plans/${pos.plan_id}?position=${pos.id}`)} />}
      {lightbox !== null && items?.[lightbox] && (
        <Lightbox fileId={items[lightbox].id} localUrl={items[lightbox].__localUrl}
          caption={items[lightbox].description || items[lightbox].original_filename}
          counter={`${lightbox + 1} / ${items.length}`}
          onPrev={lightbox > 0 ? () => setLightbox(lightbox - 1) : null}
          onNext={lightbox < items.length - 1 ? () => setLightbox(lightbox + 1) : null}
          onClose={() => setLightbox(null)} />
      )}
    </div>
  );
}

function groupByProject(items) {
  const g = new Map();
  items.forEach((it) => { const k = it.project_name || "Ohne Projekt"; if (!g.has(k)) g.set(k, []); g.get(k).push(it); });
  return [...g.entries()].sort((a, b) => a[0].localeCompare(b[0], "de"));
}

function OverviewRow({ it, navigate, view, onOpen }) {
  const open = () => {
    if (it.plan_id) { navigate(`/plans/${it.plan_id}?position=${it.position_id}${view === "defects" ? "&tab=maengel" : ""}`); }
    else if (view === "tickets" && onOpen) { onOpen(it); }
    else { navigate(`/projects/${it.project_id}`); }
  };
  return (
    <button data-testid={`overview-item-${it.id}`}
      onClick={open}
      className={`w-full text-left flex items-center gap-3 p-3 hover:bg-slate-50 ${it.__pending ? (it.__failed ? "bg-red-50/60" : "bg-amber-50/60") : ""}`}>
      {view === "tickets" ? (it.thumb_id ? <AuthImage fileId={it.thumb_id} thumb alt="" className="h-10 w-10 rounded-lg object-cover bg-slate-100 shrink-0" data-testid={`ticket-thumb-${it.id}`} /> : <div className="h-10 w-10 rounded-lg bg-slate-100 text-slate-300 flex items-center justify-center shrink-0" data-testid={`ticket-thumb-empty-${it.id}`}><ImageIcon className="h-4 w-4" /></div>) : null}
      <span className="font-mono-tech text-xs font-bold text-slate-500 shrink-0 w-16">{it.ticket_number || it.number}</span>
      <div className="min-w-0 flex-1">
        <div className="font-medium text-slate-900 truncate">{it.title || it.bezeichnung}</div>
        {it.__pending && <div className="mt-0.5"><PendingItemBadge item={it} /></div>}
        <div className="text-xs text-slate-500 truncate">{it.project_name}{it.position_number ? ` · ${it.position_number}` : ""}{it.company ? ` · ${it.company}` : ""}{it.responsible ? ` · ${it.responsible}` : ""}</div>
      </div>
      {view === "defects" && <PendingPhotoBadge defectId={it.id} />}
      {it.due_date && <span className={`text-xs shrink-0 ${it.overdue ? "text-red-600 font-semibold" : "text-slate-400"}`}>{it.due_date}</span>}
      <Badge variant="outline" className={`shrink-0 ${stClass(it.status)}`}>{(it.status || "").replace("_", " ")}</Badge>
    </button>
  );
}
